import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';

import { api, session } from '../api';

import { readIdentity, selectCurrentVendor } from './claims';
import { AuthContext, type AuthState } from './context';
import { returnTo } from './returnTo';

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  // `useSyncExternalStore` rather than a `useState` + subscribe pair: the session is mutated from
  // outside React (the auth middleware rotates tokens mid-request), and this is what guarantees a
  // render never reads a token the session has already replaced.
  const tokens = useSyncExternalStore(session.subscribe, session.get, session.get);

  const identity = useMemo(() => (tokens === null ? null : readIdentity(tokens.token)), [tokens]);

  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);

  // Re-derived on every token change, because a rotation can add or drop vendors. Keeps the current
  // selection when it survives, otherwise falls back to the first — never leaves the app scoped to a
  // vendor the new token no longer grants.
  const currentVendor = useMemo(
    () => (identity === null ? null : selectCurrentVendor(identity.vendors, selectedVendor)),
    [identity, selectedVendor],
  );

  /**
   * Ends the session server-side, then locally. It deliberately does NOT navigate or clear caches:
   * the effect below owns that, so a user-initiated sign-out and a mid-request expiry (detected deep
   * inside the auth middleware, which has no router access) converge on one code path.
   */
  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch (error) {
      // Best-effort: an already-expired access token makes this fail, and that must not trap the user
      // in a signed-in shell. Sign out locally regardless.
      console.warn(
        'Developer Portal sign-out request failed; clearing the local session anyway.',
        error,
      );
    }
    session.clear();
  }, []);

  // The single reaction to a session ending. Tracked with a ref because `identity` is derived from
  // `tokens` — both go null in the same render, so there is no rendered value left to compare
  // against; only the previous value distinguishes "just signed out" from "was never signed in".
  const wasAuthenticatedRef = useRef(tokens !== null);
  useEffect(() => {
    const isAuthenticated = tokens !== null;
    const sessionEnded = wasAuthenticatedRef.current && !isAuthenticated;
    wasAuthenticatedRef.current = isAuthenticated;
    if (!sessionEnded) return;

    // Server state belongs to the signed-out user — without this, the next sign-in briefly renders
    // the previous account's apps from cache.
    queryClient.clear();

    // Carries the deep link through, the same way the `/~` and `/admin` guards do for a visitor who
    // arrives without a session — otherwise a token that expires mid-request drops the user on the
    // dashboard root and loses the page they were actually on.
    //
    // Read off `router.state` rather than through `useRouterState`: subscribing here would re-render
    // the entire app on every navigation, and this only ever needs the location at the one instant
    // the session ended.
    void navigate({
      to: '/auth/login',
      search: { redirect: returnTo(router.state.location.href) },
    });
  }, [tokens, navigate, queryClient, router]);

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated: tokens !== null,
      identity,
      currentVendor,
      setCurrentVendor: setSelectedVendor,
      signOut,
    }),
    [tokens, identity, currentVendor, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
